import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, Illustration, toast } from '@node-space/ui-components'
import { DurationInMilliseconds } from 'constants/General'
import { useDeleteChannel } from 'hooks/mutations/useDeleteChannel'
import { Channel } from 'types/channels'

interface ChannelDeleteProps {
  channelToDelete: Channel
  handleCloseModal: () => void
  onDeleteCallback: () => void
}

export const ChannelDelete = ({
  channelToDelete,
  handleCloseModal,
  onDeleteCallback,
}: ChannelDeleteProps) => {
  const { t } = useTranslation()

  const { mutate: mutateDeleteChannel, isPending: isLoading } = useDeleteChannel()

  const handleOnDelete = async () => {
    mutateDeleteChannel(channelToDelete?.uuid, {
      onSuccess: () => {
        onDeleteCallback()
        toast({
          header: t('payments.channels.deleteSuccess', {
            reference: channelToDelete?.reference,
          }),
          variant: 'info',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
        handleCloseModal()
      },
      onError: () => {
        toast({
          header: t('oopsSomethingWentWrong'),
          variant: 'error',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
        handleCloseModal()
      },
    })
  }

  return (
    <Dialog
      title={t('payments.channels.deleteChannelTitle', { reference: channelToDelete?.reference })}
      open
      onClose={handleCloseModal}
      Illustration={<Illustration name="CloudLightning" />}
      ActionButtons={[
        <Button
          key="deleteKey"
          testId="delete-channel-button"
          variant="destructive"
          loading={isLoading}
          onClick={handleOnDelete}
        >
          {t('delete')}
        </Button>,
        <Button
          key="cancel"
          onClick={handleCloseModal}
          variant="secondary"
          testId="cancel-delete-channel-button"
        >
          {t('cancel')}
        </Button>,
      ]}
    >
      <p className="text-foreground-prominent text-body-regular text-center">
        {t('deleteDescription')}
      </p>
    </Dialog>
  )
}
